/**
 * Zwergenwald - Formular "Vertrag widerrufen" (blocks/zw-widerruf-formular.liquid).
 * Entscheidung des Inhabers 06.10.2026: Eigenbau statt App. Texte aus rechtstexte/07-widerrufsfunktion.md (B, C, D).
 *
 * Schnittstelle (gebaut vom Chat der Webseite meinzwergenwald.de):
 *   POST <data-endpunkt> mit JSON { name, bestellung, umfang: 'ganz'|'teil', artikel (nur bei 'teil'), email,
 *        website (Honeypot, leer), seite (Herkunfts-URL) }
 *   200 { ok: true, vorgang, eingang (ISO), eingangText }      -> Ergebnis D "eingegangen"
 *   400 { ok: false, fehler: { feld: meldung } }                -> zurueck zu Schritt 1, Meldung C je Feld
 *   400 nur mit fremdem Feld (z. B. Falle website) / 429 / 500 / 503 / Netzfehler / Zeitueberschreitung
 *                                                               -> Ergebnis D "noch nicht angekommen"
 *   Ohne JavaScript schickt das HTML-Formular dieselben Felder, und der Server antwortet selbst mit einer
 *   eigenen Ergebnisseite (07, Schnittstelle). Abfrageparameter wie ?widerruf=ok wertet dieses Modul bewusst
 *   NICHT aus - es gibt keine Rueckleitung mehr, ein solcher Link waere immer gebastelt.
 *
 * Ablauf wie ein kleiner Zustandsautomat in React (useState fuer den Schritt), nur dass das Markup schon
 * serverseitig gerendert ist und hier nur ein- und ausgeblendet wird:
 *   Standard (data-zusammenfassung="false"): eingabe --Widerruf bestaetigen (geprueft)--> senden --> ergebnis
 *   Nur mit Schalter an: eingabe --Weiter (geprueft)--> pruefen --Widerruf bestaetigen--> senden --> ergebnis
 * Die Eingabetaste sendet nie: Der erste Absendeknopf im Formular ist gesperrt (siehe Block).
 * Gezeigte Meldungen kommen immer aus C (freigegebener Wortlaut), auch wenn der Server eigene Texte schickt -
 * vom Server zaehlt nur, welches Feld betroffen ist.
 * Doppelklick: Waehrend eine Anfrage laeuft, wird kein zweiter POST geschickt; nach Erfolg ist das Formular weg.
 */

export const MELDUNGEN = Object.freeze({
  name: 'Bitte gib deinen Namen an.',
  bestellung: 'Bitte nenne die Bestellung – Bestellnummer oder Bestelldatum und Artikel.',
  umfang: 'Bitte wähle, ob du die ganze Bestellung oder einzelne Artikel widerrufst.',
  artikel: 'Bitte nenne die Artikel, die du widerrufst.',
  email: 'Bitte gib eine gültige E-Mail-Adresse an, damit wir dir die Eingangsbestätigung schicken können.',
});

const FELDER = Object.keys(MELDUNGEN);
const GRENZEN = Object.freeze({ name: [2, 200], bestellung: [1, 500], artikel: [1, 1000], email: [3, 254] });
const EMAIL_MUSTER = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ZEITLIMIT_MS = 20000;
const UMFANG_TEXT = Object.freeze({ ganz: 'Die ganze Bestellung', teil: 'Nur diese Artikel: ' });
const SENDE_STATUS = 'Dein Widerruf wird gesendet …';

/** Prueft die Angaben wie der Server und liefert { feld: Meldung aus C } fuer jedes fehlende Feld. */
export function pruefeAngaben(werte) {
  const fehler = {};
  const passt = (feld) => {
    const laenge = String(werte[feld] ?? '').length;
    const [min, max] = GRENZEN[feld];
    return laenge >= min && laenge <= max;
  };
  if (!passt('name')) fehler.name = MELDUNGEN.name;
  if (!passt('bestellung')) fehler.bestellung = MELDUNGEN.bestellung;
  if (werte.umfang !== 'ganz' && werte.umfang !== 'teil') fehler.umfang = MELDUNGEN.umfang;
  if (werte.umfang === 'teil' && !passt('artikel')) fehler.artikel = MELDUNGEN.artikel;
  if (!passt('email') || !EMAIL_MUSTER.test(String(werte.email ?? ''))) fehler.email = MELDUNGEN.email;
  return fehler;
}

/** Ordnet eine Server-Antwort einem der drei Ausgaenge zu: ok, felder (400) oder fehler. */
export function deuteAntwort(status, daten) {
  if (status === 200 && daten && daten.ok === true) {
    return {
      art: 'ok',
      vorgang: typeof daten.vorgang === 'string' || typeof daten.vorgang === 'number' ? String(daten.vorgang) : '',
      eingang: typeof daten.eingang === 'string' ? daten.eingang : '',
      eingangText: typeof daten.eingangText === 'string' ? daten.eingangText.trim() : '',
    };
  }
  if (status === 400 && daten && daten.fehler && typeof daten.fehler === 'object') {
    const felder = {};
    for (const feld of FELDER) {
      if (Object.prototype.hasOwnProperty.call(daten.fehler, feld)) felder[feld] = MELDUNGEN[feld];
    }
    if (Object.keys(felder).length > 0) return { art: 'felder', felder };
  }
  const grund = status === 429 ? 'zu-viele-anfragen' : (daten && typeof daten.grund === 'string' && daten.grund) || `status-${status}`;
  return { art: 'fehler', grund };
}

/** Rueckfall, falls eingangText fehlt: ISO-Zeit als "06.10.2026 um 22:15 Uhr (MESZ)", Zeitzone Europe/Berlin. */
export function eingangAlsText(iso) {
  if (!iso) return '';
  const zeit = new Date(iso);
  if (Number.isNaN(zeit.getTime())) return '';
  try {
    const teile = {};
    const format = new Intl.DateTimeFormat('de-DE', {
      timeZone: 'Europe/Berlin',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
      timeZoneName: 'short',
    });
    for (const teil of format.formatToParts(zeit)) teile[teil.type] = teil.value;
    return `${teile.day}.${teile.month}.${teile.year} um ${teile.hour}:${teile.minute} Uhr (${teile.timeZoneName})`;
  } catch {
    return '';
  }
}

class ZwWiderruf extends HTMLElement {
  #bereit = false;
  #laeuft = false; // eine Anfrage ist unterwegs
  #erledigt = false; // Erfolg angezeigt, nichts mehr senden
  #schritt = 'eingabe';
  #mitZusammenfassung = true;
  #endpunkt = '';
  #formular = null;

  connectedCallback() {
    if (this.#bereit) return;
    this.#formular = this.querySelector('[data-zw-wr-formular]');
    if (!this.#formular) return;
    this.#bereit = true;

    this.#endpunkt = this.getAttribute('data-endpunkt') || this.#formular.getAttribute('action') || '';
    this.#mitZusammenfassung = this.getAttribute('data-zusammenfassung') !== 'false';
    // Ab hier prueft das Skript selbst und zeigt die Meldungen aus C statt der Browser-Blasen.
    this.#formular.noValidate = true;

    this.#formular.addEventListener('submit', (ereignis) => this.#beimAbsenden(ereignis));
    this.addEventListener('click', (ereignis) => this.#beimKlick(ereignis));
    this.addEventListener('change', (ereignis) => this.#beiAenderung(ereignis));
    this.addEventListener('input', (ereignis) => this.#beiEingabe(ereignis));

    const seite = this.#feld('seite');
    if (seite && globalThis.location) seite.value = `${location.origin}${location.pathname}`;

    this.#artikelfeldAnpassen();
    this.#zeigeSchritt('eingabe', false);
  }

  /* ---------- Lesen ---------- */

  #feld(name) {
    return this.querySelector(`[name="${name}"]`);
  }

  #werte() {
    const text = (name) => String(this.#feld(name)?.value ?? '').trim();
    const gewaehlt = Array.from(this.querySelectorAll('[name="umfang"]')).find((knopf) => knopf.checked);
    return {
      name: text('name'),
      bestellung: text('bestellung'),
      umfang: gewaehlt ? gewaehlt.value : '',
      artikel: text('artikel'),
      email: text('email'),
      website: String(this.#feld('website')?.value ?? ''),
      seite: text('seite'),
    };
  }

  /* ---------- Ereignisse ---------- */

  #beimKlick(ereignis) {
    const ziel = ereignis.target && ereignis.target.closest ? ereignis.target : null;
    if (!ziel) return;
    if (ziel.closest('[data-zw-wr-weiter]')) {
      ereignis.preventDefault();
      this.#weiter();
    } else if (ziel.closest('[data-zw-wr-aendern]')) {
      ereignis.preventDefault();
      if (this.#laeuft || this.#erledigt) return;
      this.#verbergeErgebnisse();
      this.#zeigeSchritt('eingabe', false);
      this.#feld('name')?.focus();
    }
  }

  #beiAenderung(ereignis) {
    const name = ereignis.target?.getAttribute?.('name');
    if (name !== 'umfang') return;
    this.#meldung('umfang', '');
    this.#artikelfeldAnpassen();
  }

  #beiEingabe(ereignis) {
    const name = ereignis.target?.getAttribute?.('name');
    if (FELDER.includes(name)) this.#meldung(name, '');
  }

  #beimAbsenden(ereignis) {
    ereignis.preventDefault();
    if (this.#laeuft || this.#erledigt) return;
    if (this.#mitZusammenfassung && this.#schritt !== 'pruefen') {
      this.#weiter(); // Enter in einem Feld von Schritt 1 fuehrt zur Zusammenfassung, nie direkt zum Senden
      return;
    }
    const werte = this.#werte();
    const fehler = pruefeAngaben(werte);
    if (Object.keys(fehler).length > 0) {
      this.#zeigeSchritt('eingabe', false);
      this.#zeigeFehler(fehler);
      return;
    }
    this.#verbergeErgebnisse(); // alte Liste C, Hilfstext oder D (Fehler) weg, bevor neu gesendet wird
    this.#senden(werte);
  }

  /* ---------- Schritte ---------- */

  #weiter() {
    if (this.#laeuft || this.#erledigt) return;
    const werte = this.#werte();
    const fehler = pruefeAngaben(werte);
    this.#zeigeFehler(fehler);
    if (Object.keys(fehler).length > 0) return;
    this.#verbergeErgebnisse();
    if (!this.#mitZusammenfassung) {
      this.#senden(werte);
      return;
    }
    this.#fuelleZusammenfassung(werte);
    this.#zeigeSchritt('pruefen', true);
  }

  #zeigeSchritt(schritt, fokus) {
    this.#schritt = schritt;
    const pruefen = schritt === 'pruefen';
    this.#setzeVerborgen('[data-zw-wr-eingabe]', pruefen);
    this.#setzeVerborgen('[data-zw-wr-pruefen]', !pruefen);
    this.#setzeVerborgen('[data-zw-wr-weiter-bereich]', pruefen || !this.#mitZusammenfassung);
    this.#setzeVerborgen('[data-zw-wr-abschluss]', this.#mitZusammenfassung && !pruefen);
    this.#setzeVerborgen('[data-zw-wr-aendern]', !pruefen);
    if (fokus && pruefen) this.querySelector('[data-zw-wr-pruefen-titel]')?.focus();
  }

  #fuelleZusammenfassung(werte) {
    const zeige = (feld, text) => {
      const ziel = this.querySelector(`[data-zw-wr-zeige="${feld}"]`);
      if (ziel) ziel.textContent = text;
    };
    zeige('name', werte.name);
    zeige('bestellung', werte.bestellung);
    zeige('umfang', werte.umfang === 'teil' ? `${UMFANG_TEXT.teil}${werte.artikel}` : UMFANG_TEXT.ganz);
    zeige('email', werte.email);
  }

  #artikelfeldAnpassen() {
    const teil = this.#werte().umfang === 'teil';
    this.#setzeVerborgen('[data-zw-wr-artikel]', !teil);
    const artikel = this.#feld('artikel');
    if (!artikel) return;
    if (teil) artikel.setAttribute('required', '');
    else {
      artikel.removeAttribute('required');
      this.#meldung('artikel', '');
    }
  }

  /* ---------- Meldungen ---------- */

  #meldung(feld, text) {
    const ziel = this.querySelector(`[data-zw-wr-meldung="${feld}"]`);
    if (ziel) {
      ziel.textContent = text;
      ziel.hidden = !text;
    }
    const eingaben = feld === 'umfang' ? Array.from(this.querySelectorAll('[name="umfang"]')) : [this.#feld(feld)];
    for (const eingabe of eingaben) {
      if (!eingabe) continue;
      if (text) eingabe.setAttribute('aria-invalid', 'true');
      else eingabe.removeAttribute('aria-invalid');
    }
  }

  #zeigeFehler(fehler) {
    for (const feld of FELDER) this.#meldung(feld, fehler[feld] || '');
    const erstes = FELDER.find((feld) => fehler[feld]);
    if (!erstes) return;
    const ziel = erstes === 'umfang' ? this.querySelector('[name="umfang"]') : this.#feld(erstes);
    ziel?.focus();
  }

  /* ---------- Senden ---------- */

  async #senden(werte) {
    if (this.#laeuft || this.#erledigt) return;
    this.#laeuft = true;
    this.#beschaeftigt(true);

    const nutzdaten = {
      name: werte.name,
      bestellung: werte.bestellung,
      umfang: werte.umfang,
      ...(werte.umfang === 'teil' ? { artikel: werte.artikel } : {}),
      email: werte.email,
      website: werte.website,
      seite: werte.seite,
    };

    let ergebnis;
    const steuerung = typeof AbortController === 'function' ? new AbortController() : null;
    const uhr = steuerung ? setTimeout(() => steuerung.abort(), ZEITLIMIT_MS) : null;
    try {
      const antwort = await fetch(this.#endpunkt, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(nutzdaten),
        signal: steuerung ? steuerung.signal : undefined,
      });
      let daten = null;
      try {
        daten = await antwort.json();
      } catch {
        daten = null;
      }
      ergebnis = deuteAntwort(antwort.status, daten);
    } catch {
      ergebnis = { art: 'fehler', grund: 'netz' };
    } finally {
      if (uhr) clearTimeout(uhr);
      this.#laeuft = false;
      this.#beschaeftigt(false);
    }

    if (ergebnis.art === 'ok') {
      this.#zeigeErfolg(ergebnis, werte.email);
    } else if (ergebnis.art === 'felder') {
      this.#zeigeSchritt('eingabe', false);
      this.#zeigeFehler(ergebnis.felder);
    } else {
      this.#zeigeFehlschlag();
    }
  }

  #beschaeftigt(an) {
    this.#formular?.setAttribute('aria-busy', an ? 'true' : 'false');
    for (const knopf of this.querySelectorAll('[data-zw-wr-senden], [data-zw-wr-aendern]')) {
      if (an) knopf.setAttribute('aria-disabled', 'true');
      else knopf.removeAttribute('aria-disabled');
    }
    const status = this.querySelector('[data-zw-wr-status]');
    if (status) status.textContent = an ? SENDE_STATUS : '';
  }

  /* ---------- Ergebnis D ---------- */

  #zeigeErfolg(ergebnis, email) {
    this.#erledigt = true;
    if (this.#formular) this.#formular.hidden = true;

    const eingangText = ergebnis.eingangText || eingangAlsText(ergebnis.eingang);
    const eingang = this.querySelector('[data-zw-wr-eingang]');
    if (eingang) eingang.textContent = eingangText;
    this.#setzeVerborgen('[data-zw-wr-eingang-zeile]', !eingangText);

    // Die Vorgangsnummer steht nicht in D (07); sie kommt nur in der Eingangsbestaetigung E vor.
    const adresse = this.querySelector('[data-zw-wr-adresse]');
    if (adresse) adresse.textContent = email || 'die angegebene E-Mail-Adresse';

    this.#zeigeErgebnis('ok');
  }

  #zeigeFehlschlag() {
    this.#zeigeErgebnis('fehler');
  }

  /** Zeigt genau einen Ergebnisblock (ok oder fehler) und setzt den Fokus auf seine Ueberschrift. */
  #zeigeErgebnis(art) {
    for (const block of this.querySelectorAll('[data-zw-wr-ergebnis]')) {
      block.hidden = block.getAttribute('data-zw-wr-ergebnis') !== art;
    }
    const block = this.querySelector(`[data-zw-wr-ergebnis="${art}"]`);
    if (block) (block.querySelector('h2') || block).focus();
  }

  #verbergeErgebnisse() {
    for (const block of this.querySelectorAll('[data-zw-wr-ergebnis]')) {
      if (block.getAttribute('data-zw-wr-ergebnis') !== 'ok') block.hidden = true;
    }
  }

  #setzeVerborgen(selektor, verborgen) {
    const element = this.querySelector(selektor);
    if (element) element.hidden = verborgen;
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('zw-widerruf')) {
  customElements.define('zw-widerruf', ZwWiderruf);
}
